import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { authRoutes, routes as dashboardRoutes, errorRoutes } from './modules'
import { useAuthStore } from '@/modules/auth'
import { hasPermission } from '@/core/composables/usePermissions'
import { useEnabledModules } from '@/core/composables/useEnabledModules'

/** Protección por permisos del usuario (`user.permissions`), alineado con el backend. */
type TMetaAuthOptions = {
  requiredPermission?: string | string[]
  requiredModule?: string
  /** Solo tenant central (`user.tenant.is_central`). */
  requiredCentral?: boolean
  redirect?: string
  forbiddenRedirect?: string
}

const routes: Array<RouteRecordRaw> = [
  ...errorRoutes,
  ...authRoutes,
  ...dashboardRoutes,
]

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior() {
    return { top: 0 }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Actualizar el título de la página
  document.title = `${import.meta.env.VITE_APP_NAME} ${to.meta?.title ? ' | ' + to.meta.title : ''}`

  // Obtener la configuración de autenticación de la ruta (puede ser boolean o TMetaAuthOptions)
  const requiresAuth = to.meta?.auth as boolean | TMetaAuthOptions

  if (!requiresAuth) {
    // Ruta pública, permitir acceso
    return next()
  }

  // Sin usuario en RAM: intentar restaurar sesión por cookie (p. ej. F5)
  if (!authStore.isAuthenticated) {
    await authStore.checkAuth()
  }

  // Verificar si, después de todo, seguimos sin estar autenticados
  if (!authStore.isAuthenticated) {
    const redirectOptions =
      typeof requiresAuth === 'object' ? requiresAuth.redirect : null
    const redirect = redirectOptions || '/auth/login'
    return next({ path: redirect })
  }

  // Si la ruta solo requiere estar logueado, sin validar permisos
  if (typeof requiresAuth === 'boolean' && requiresAuth) {
    return next()
  }

  // Objeto de auth: opcionalmente exige un permiso del RBAC
  if (typeof requiresAuth === 'object') {
    const requiredModule = requiresAuth.requiredModule
    if (requiredModule) {
      const { isModuleEnabled } = useEnabledModules()
      if (!isModuleEnabled(requiredModule)) {
        const forbiddenRedirect = requiresAuth.forbiddenRedirect || '/403'
        return next({ path: forbiddenRedirect })
      }
    }

    if (requiresAuth.requiredCentral && !authStore.user?.tenant?.is_central) {
      const forbiddenRedirect = requiresAuth.forbiddenRedirect || '/403'
      return next({ path: forbiddenRedirect })
    }

    const required = requiresAuth.requiredPermission
    const needsCheck =
      typeof required === 'string'
        ? required.length > 0
        : Array.isArray(required) && required.length > 0

    if (
      needsCheck &&
      !hasPermission({
        permission: required as string | string[],
        mode: 'any',
      })
    ) {
      const forbiddenRedirect = requiresAuth.forbiddenRedirect || '/403'
      return next({ path: forbiddenRedirect })
    }

    return next()
  }

  // Permitir el acceso por defecto
  return next()
})

export default router
